import { useState, useEffect, useMemo } from "react";
import {
  useCreatePricelist,
  useOnePricelist,
  useUpdatePricelist,
  usePricelistsByVendor,
} from "http-hooks/pricelist/usePricelist";
import { SelectVendors } from "components/ui/selectors";
import { Switch } from "@headlessui/react";
import Loading from "components/Loading";
import Select from "components/ui/Select";
import FileUploader from "components/ui/FileUploader";
import Input from "components/ui/Input";
import useAuthStore from "store/auth";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import Modal from "components/ui/modals";

// Constants moved to top to prevent re-renders
const typeOptions = [
  { value: "P", label: "Purchase" },
  { value: "S", label: "Sales" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SetPricelist({ id, onClose, open }) {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedSourcePricelist, setSelectedSourcePricelist] = useState(null);
  // Get user type from auth store
  const agttp = useAuthStore((state) => state.agttp);
  const isAgent = agttp === 0;
  
  const handleClose = () => {
    if (onClose) onClose();
  };
  
  // Get company preferences
  const { isDocUploaderEnabled } = useCompanyPreferences();

  const [pricelistDetails, setPricelistDetails] = useState({
    supid: "",
    cirdt: new Date().toISOString().split("T")[0],
    effrctivedt: new Date().toISOString().split("T")[0],
    pl_remark: "",
    ag_status: 1,
    typ: isAgent ? "G" : "P", // G for agents, P default for traders
  });

  // Show vendor box only for agents or when type is Purchase
  const shouldShowVendor = isAgent || pricelistDetails.typ === "P";

  const { isLoading, data } = useOnePricelist(id);
  const { data: vendorPricelists } = usePricelistsByVendor(
    !id ? pricelistDetails.supid : null
  );
  const { mutateAsync: mutateCreate, isPending: isPricelistCreating } =
    useCreatePricelist();
  const { mutateAsync: mutateUpdate, isPending: isPricelistUpdating } =
    useUpdatePricelist();

  const docFilename = useMemo(() => {
    if (!pricelistDetails.supid || !pricelistDetails.effrctivedt) return "";
    return `${pricelistDetails.supid}_${pricelistDetails.effrctivedt}`;
  }, [pricelistDetails.supid, pricelistDetails.effrctivedt]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPricelistDetails((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleStatusChange = (e) => {
    const val = e ? 1 : 0;
    setPricelistDetails((prevData) => ({
      ...prevData,
      ag_status: val,
    }));
  };

  const handleVendorChange = (vendor) => {
    setSelectedAccount(vendor);
    setPricelistDetails((prevData) => ({
      ...prevData,
      supid: vendor?.ps_Id || "",
    }));
  };

  const handleTypeChange = (type) => {
    setPricelistDetails((prevData) => ({
      ...prevData,
      typ: type?.value || "P",
      // Clear vendor if switching to Sales type
      ...(type?.value === "S" && { supid: "" }),
    }));
    // Clear selected vendor if switching to Sales type
    if (type?.value === "S") {
      setSelectedAccount(null);
    }
  };

  useEffect(() => {
    if (data && id) {
      setPricelistDetails({
        supid: data.supid || "",
        cirdt: data.cirdt ? data.cirdt.split("T")[0] : new Date().toISOString().split("T")[0],
        effrctivedt: data.effrctivedt ? data.effrctivedt.split("T")[0] : new Date().toISOString().split("T")[0],
        pl_remark: data.pl_remark || "",
        ag_status: data.ag_status,
        pictlink: data.pictlink || "",
        typ: data.typ || (isAgent ? "G" : "P"),
      });
      
      // Set the selected vendor from the response
      if (data.vendor) {
        setSelectedAccount(data.vendor);
      }
    }
  }, [data, id, isAgent]);

  const pricelistOptions = useMemo(() => {
    return vendorPricelists?.map(pricelist => ({
      value: pricelist.rateid,
      label: `ID: ${pricelist.rateid} | Circular Date: ${new Date(pricelist.cirdt).toLocaleDateString()} | Effective Date: ${new Date(pricelist.effrctivedt).toLocaleDateString()}`
    })) || [];
  }, [vendorPricelists]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: pricelistDetails });
      } else {
        const createData = {
          ...pricelistDetails,
          ...(selectedSourcePricelist && { fromPricelistId: selectedSourcePricelist.value }),
        };
        await mutateCreate({ data: createData });
      }
      handleClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  const isPending = isPricelistCreating || isPricelistUpdating;

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      maxWidth="xl"
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Pricelist
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-pricelist-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      <form id="set-pricelist-form" onSubmit={handleSubmit}>
          {isLoading && !data ? (
            <Loading />
          ) : (
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
              {!isAgent && (
                <div className="col-span-full">
                  <Select
                    value={typeOptions.find(option => option.value === pricelistDetails.typ) || null}
                    onChange={handleTypeChange}
                    options={typeOptions}
                    label="Type"
                    placeholder="Select type"
                    isDisabled={Boolean(id)}
                    isRequired={true}
                  />
                </div>
              )}
              {shouldShowVendor && (
                <div className="col-span-full">
                  <SelectVendors
                    value={selectedAccount}
                    onChange={handleVendorChange}
                    placeholder="Select vendor"
                    isDisabled={Boolean(id)}
                    label="Vendor Name"
                    isRequired={pricelistDetails.typ === "P"}
                  />
                </div>
              )}
              <div className="sm:col-span-3">
                <Input
                  type="date"
                  name="cirdt"
                  value={pricelistDetails.cirdt}
                  onChange={handleInputChange}
                  label="Circular Date"
                  isRequired={true}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  type="date"
                  name="effrctivedt"
                  value={pricelistDetails.effrctivedt}
                  onChange={handleInputChange}
                  label="Effective Date"
                  isRequired={true}
                />
              </div>
              <div className="col-span-full">
                <label
                  htmlFor="pl_remark"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Remark
                </label>
                <div className="mt-2">
                  <textarea
                    type="text"
                    placeholder="Remark"
                    name="pl_remark"
                    id="pl_remark"
                    rows={3}
                    value={pricelistDetails.pl_remark}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              {!id && pricelistDetails.supid && pricelistOptions.length > 0 && (
                <div className="col-span-full">
                  <Select
                    value={selectedSourcePricelist}
                    onChange={setSelectedSourcePricelist}
                    options={pricelistOptions}
                    label="Copy Pricelist From"
                    placeholder="Select a pricelist to copy from"
                    isClearable={true}
                    isSearchable={true}
                  />
                </div>
              )}
              {isDocUploaderEnabled() && docFilename && (
                <div className="col-span-full">
                  <FileUploader docFilename={docFilename} />
                </div>
              )}
              {id && (
                <div className="sm:col-span-3">
                  <label
                    htmlFor="ag_status"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Status
                  </label>
                  <Switch
                    checked={pricelistDetails.ag_status}
                    onChange={handleStatusChange}
                    className={classNames(
                      pricelistDetails.ag_status
                        ? "bg-indigo-600"
                        : "bg-gray-200",
                      "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        pricelistDetails.ag_status
                          ? "translate-x-5"
                          : "translate-x-0",
                        "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                      )}
                    />
                  </Switch>
                </div>
              )}
            </div>
          )}
      </form>
    </Modal>
  );
}
